import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Box,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  Divider,
  CircularProgress,
} from "@mui/material";
import { Warning, Add, Edit, AllInclusive } from "@mui/icons-material";
import { useUserAccounts } from "../../../stores/User/User.hooks";
import { useRxsForPatient, useRx } from "../../../stores/Rx/rx.queries";
import { RxLightDto } from "../../../api-client";
import { useMasterTables, getTarifGroupForRxType as utilGetTarifGroupForRxType } from "../../../stores/MasterTables";

// ============================================================================
// Types
// ============================================================================

export interface PreflightResult {
  /** Account (Mandant) ID selected by the user */
  accountId: string;
  /** If set, user chose to update this existing Rx instead of creating a new one */
  existingRxId: string | null;
  /** Selected RxType id (e.g. "Kasse", "Zahnarzt") */
  rxType: string;
  /** Selected TarifGroup id (e.g. "GKV") */
  tarifGroupId: string;
  /** True when the selected existing Rx is a Dauerrezept that should be unvirtualized */
  unvirtualize: boolean;
}

interface RxSavePreflightDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (result: PreflightResult) => void;
  patientId: number;
  /** Extracted prescription date (for duplicate detection) */
  extractionDate?: string | null;
  /** Extracted therapy abbreviation */
  extractionTherapy?: string | null;
  /** Extracted treatment unit count */
  extractionAmount?: number | null;
  /** Inferred RxType id from OCR text (e.g. "Kasse") */
  inferredRxType?: string | null;
  /** True when the parent is currently saving */
  saving?: boolean;
}

// ============================================================================
// Helpers
// ============================================================================

/** Normalize date strings to yyyy-mm-dd for comparison */
const normalizeDate = (d: string | null | undefined): string | null => {
  if (!d) return null;
  // ISO yyyy-mm-dd
  const iso = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  // German dd.mm.yyyy
  const de = d.match(/^(\d{2})\.(\d{2})\.(\d{4})/);
  if (de) return `${de[3]}-${de[2]}-${de[1]}`;
  return null;
};

const formatDateDisplay = (d: string | null | undefined): string => {
  if (!d) return "kein Datum";
  const iso = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}.${iso[2]}.${iso[1]}`;
  return d;
};

interface RxMatch {
  rx: RxLightDto;
  reason: "exact-date" | "no-date-therapy-match" | "perpetual-therapy-match" | "perpetual-no-therapy";
}

// ============================================================================
// Sub-component: fetches full RxDto for ICD codes
// ============================================================================

const RxMatchSubtext: React.FC<{ rx: RxLightDto }> = ({ rx }) => {
  const { data: fullRx } = useRx(rx.id);
  const icdCodes = fullRx?.icdCodeEntries
    ?.map((e) => e.userText)
    .filter(Boolean);

  return (
    <Typography variant="caption" color="text.secondary" sx={{ ml: 3.5 }} component="div">
      Ausstellungsdatum: {formatDateDisplay(rx.date)}
      {rx.createdAt && ` · Eingetragen: ${formatDateDisplay(rx.createdAt)}`}
      {rx.isPerpetual && " · Dauerrezept ∞"}
      <br />
      {rx.therapyRxs
        ?.map((t) => `${t.therapyAbbr} ×${t.amount}`)
        .join(", ") || "keine Heilmittel"}
      {icdCodes && icdCodes.length > 0 && (
        <> · ICD-10: {icdCodes.join(", ")}</>
      )}
    </Typography>
  );
};

// ============================================================================
// Component
// ============================================================================

const RxSavePreflightDialog: React.FC<RxSavePreflightDialogProps> = ({
  open,
  onClose,
  onConfirm,
  patientId,
  extractionDate,
  extractionTherapy,
  extractionAmount,
  inferredRxType,
  saving,
}) => {
  const accounts = useUserAccounts();
  const { data: existingRxs, isLoading: rxsLoading } = useRxsForPatient(patientId);
  const { data: masterTablesData } = useMasterTables();

  const [selectedAccountId, setSelectedAccountId] = useState<string>("");
  const [saveMode, setSaveMode] = useState<"new" | string>("new"); // "new" or rxId
  const [selectedRxType, setSelectedRxType] = useState<string>("");
  const [selectedTarifGroup, setSelectedTarifGroup] = useState<string>("");
  const [tarifGroupOptions, setTarifGroupOptions] = useState<string[]>([]);

  const getTarifGroupForRxType = (rxTypeId: string) =>
    utilGetTarifGroupForRxType(masterTablesData || [], rxTypeId);

  const rxTypeOptions = (masterTablesData || [])
    .filter((obj: any) => "rxType" in obj)
    .flatMap((obj: any) => obj.rxType as Array<{ id: string; text: string }>);

  // ── Duplicate / match detection ─────────────────────────────
  const matches = useMemo<RxMatch[]>(() => {
    if (!existingRxs || !extractionDate) return [];
    const extDate = normalizeDate(extractionDate);
    const result: RxMatch[] = [];

    for (const rx of existingRxs) {
      if (rx.deletedAt) continue;

      const rxDate = normalizeDate(rx.date ?? null);

      // Case 1: Exact date match → likely duplicate
      if (extDate && rxDate && extDate === rxDate) {
        result.push({ rx, reason: "exact-date" });
        continue;
      }

      const therapyMatch = extractionTherapy
        ? rx.therapyRxs?.some(
            (t) =>
              t.therapyAbbr?.toLowerCase() === extractionTherapy.toLowerCase() &&
              (extractionAmount == null || t.amount === extractionAmount)
          )
        : false;

      // Case 2: Perpetual (Dauerrezept) Rx — strongest match for scanned Rx data
      if (rx.isPerpetual && !rxDate) {
        result.push({
          rx,
          reason: therapyMatch ? "perpetual-therapy-match" : "perpetual-no-therapy",
        });
        continue;
      }

      // Case 3: Non-perpetual Rx with no date, but therapy+amount match
      // (phone-entered Rx that needs to be completed with scanned data)
      if (!rxDate && therapyMatch) {
        result.push({ rx, reason: "no-date-therapy-match" });
      }
    }

    // Sort: perpetual therapy matches first, then perpetual without therapy, then rest
    const priority: Record<RxMatch["reason"], number> = {
      "perpetual-therapy-match": 0,
      "perpetual-no-therapy": 1,
      "no-date-therapy-match": 2,
      "exact-date": 3,
    };
    result.sort((a, b) => priority[a.reason] - priority[b.reason]);

    return result;
  }, [existingRxs, extractionDate, extractionTherapy, extractionAmount]);

  // Auto-select first account if only one
  React.useEffect(() => {
    if (accounts?.length === 1 && !selectedAccountId) {
      setSelectedAccountId(accounts[0].id);
    }
  }, [accounts, selectedAccountId]);

  // Reset state when dialog opens
  React.useEffect(() => {
    if (open) {
      // Auto-select perpetual match with therapy match as the default
      const perpetualTherapyMatch = matches.find(
        (m) => m.reason === "perpetual-therapy-match"
      );
      setSaveMode(perpetualTherapyMatch ? perpetualTherapyMatch.rx.id : "new");
      if (accounts?.length === 1) {
        setSelectedAccountId(accounts[0].id);
      } else {
        setSelectedAccountId("");
      }
      // Pre-select rxType from inference
      if (inferredRxType) {
        setSelectedRxType(inferredRxType);
        const groups = getTarifGroupForRxType(inferredRxType);
        setTarifGroupOptions(groups);
        setSelectedTarifGroup(groups[0] || "");
      } else {
        setSelectedRxType("");
        setTarifGroupOptions([]);
        setSelectedTarifGroup("");
      }
    }
  }, [open, accounts, inferredRxType, matches]);

  const handleRxTypeChange = (rxTypeId: string) => {
    setSelectedRxType(rxTypeId);
    const groups = getTarifGroupForRxType(rxTypeId);
    setTarifGroupOptions(groups);
    setSelectedTarifGroup(groups[0] || "");
  };

  const selectedMatch = matches.find((m) => m.rx.id === saveMode);
  const isUnvirtualize =
    !!selectedMatch &&
    (selectedMatch.reason === "perpetual-therapy-match" ||
      selectedMatch.reason === "perpetual-no-therapy");

  const handleConfirm = () => {
    onConfirm({
      accountId: selectedAccountId,
      existingRxId: saveMode === "new" ? null : saveMode,
      rxType: selectedRxType,
      tarifGroupId: selectedTarifGroup,
      unvirtualize: isUnvirtualize,
    });
  };

  const isNewRx = saveMode === "new";
  const canConfirm =
    (isNewRx ? !!selectedAccountId : true) &&
    !!selectedRxType &&
    !!selectedTarifGroup;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Rezept speichern</DialogTitle>

      <DialogContent dividers>
        {/* ── RxType & TarifGroup ────────────────────────── */}
        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <FormControl fullWidth size="small" required>
            <InputLabel>Rezept-Typ</InputLabel>
            <Select
              value={selectedRxType}
              label="Rezept-Typ"
              onChange={(e) => handleRxTypeChange(e.target.value)}
            >
              {rxTypeOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" required>
            <InputLabel>Tarifgruppe</InputLabel>
            <Select
              value={selectedTarifGroup}
              label="Tarifgruppe"
              onChange={(e) => setSelectedTarifGroup(e.target.value)}
            >
              {tarifGroupOptions.map((g) => (
                <MenuItem key={g} value={g}>
                  {g}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* ── Duplicate Detection ───────────────────────── */}
        {rxsLoading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Vorhandene Rezepte werden geprüft…
            </Typography>
          </Box>
        ) : matches.length > 0 ? (
          <Box>
            <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
              {matches.length === 1
                ? "Ein möglicherweise passendes Rezept wurde gefunden."
                : `${matches.length} möglicherweise passende Rezepte gefunden.`}
            </Alert>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Was möchten Sie tun?
            </Typography>

            <RadioGroup
              value={saveMode}
              onChange={(e) => setSaveMode(e.target.value)}
            >
              <FormControlLabel
                value="new"
                control={<Radio size="small" />}
                label={
                  <Box sx={{ ml: 0.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Add fontSize="small" />
                      <Typography variant="body2">Neues Rezept erstellen</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 3.5 }} component="div">
                      Ausstellungsdatum: {formatDateDisplay(extractionDate)}
                      <br />
                      {extractionTherapy
                        ? `${extractionTherapy}${extractionAmount != null ? ` ×${extractionAmount}` : ""}`
                        : "keine Heilmittel"}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: "flex-start", mb: 1 }}
              />

              {saveMode === "new" && (
                <FormControl fullWidth size="small" required sx={{ ml: 3.5, mt: 1, mb: 1, maxWidth: 300 }}>
                  <InputLabel>Mandant</InputLabel>
                  <Select
                    value={selectedAccountId}
                    label="Mandant"
                    onChange={(e) => setSelectedAccountId(e.target.value)}
                  >
                    {accounts?.map((acc) => (
                      <MenuItem key={acc.id} value={acc.id}>
                        {acc.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Divider sx={{ my: 1 }} />

              {matches.map(({ rx, reason }) => {
                const isPerpetualMatch = reason === "perpetual-therapy-match" || reason === "perpetual-no-therapy";

                return (
                  <FormControlLabel
                    key={rx.id}
                    value={rx.id}
                    control={<Radio size="small" />}
                    label={
                      <Box sx={{ ml: 0.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                          <Edit fontSize="small" color="action" />
                          <Typography variant="body2">
                            Rezept {rx.rxNumber} aktualisieren
                          </Typography>
                          {isPerpetualMatch && (
                            <Chip
                              size="small"
                              icon={<AllInclusive sx={{ fontSize: 14 }} />}
                              color="info"
                              label="Dauerrezept"
                            />
                          )}
                          <Chip
                            size="small"
                            variant={reason === "exact-date" ? "outlined" : "filled"}
                            color={
                              reason === "exact-date"
                                ? "error"
                                : reason === "perpetual-therapy-match"
                                ? "success"
                                : "warning"
                            }
                            label={
                              reason === "exact-date"
                                ? "gleiches Datum"
                                : reason === "perpetual-therapy-match"
                                ? "Heilmittel stimmt überein"
                                : reason === "perpetual-no-therapy"
                                ? "kein Datum"
                                : "Telefonrezept ohne Datum"
                            }
                          />
                        </Box>
                        {isPerpetualMatch && (
                          <Alert severity="info" sx={{ mt: 1, mb: 0.5, py: 0 }} icon={<AllInclusive fontSize="small" />}>
                            <Typography variant="caption">
                              Dieses Dauerrezept wird mit den gescannten Daten vervollständigt.
                              Es wird automatisch ein neues Dauerrezept (ohne Datum) als Folgerezept erstellt.
                            </Typography>
                          </Alert>
                        )}
                        <RxMatchSubtext rx={rx} />
                      </Box>
                    }
                    sx={{ alignItems: "flex-start", mb: 1 }}
                  />
                );
              })}
            </RadioGroup>
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Keine vorhandenen Rezepte mit gleichem Datum gefunden — ein neues Rezept wird erstellt.
            </Typography>

            <FormControl fullWidth size="small" required>
              <InputLabel>Mandant</InputLabel>
              <Select
                value={selectedAccountId}
                label="Mandant"
                onChange={(e) => setSelectedAccountId(e.target.value)}
              >
                {accounts?.map((acc) => (
                  <MenuItem key={acc.id} value={acc.id}>
                    {acc.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!canConfirm || saving}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
          color={saveMode === "new" ? "success" : "primary"}
        >
          {saving
            ? "Wird gespeichert…"
            : saveMode === "new"
            ? "Neues Rezept erstellen"
            : isUnvirtualize
            ? "Dauerrezept vervollständigen"
            : "Rezept aktualisieren"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RxSavePreflightDialog;
